import type { MediaAsset } from '@cinegrid/core';
import { putMediaBlob } from '../media/mediaStore';

/** Probe a local File into a MediaAsset — never uploads. Shared via IndexedDB for dual tabs. */
export async function importLocalFile(file: File): Promise<MediaAsset> {
  const url = URL.createObjectURL(file);
  const mime = file.type || 'application/octet-stream';
  const kind: MediaAsset['kind'] = mime.startsWith('audio/')
    ? 'audio'
    : mime.startsWith('image/')
      ? 'image'
      : 'video';

  const id = crypto.randomUUID();
  try {
    await putMediaBlob(id, file, mime, file.name);
  } catch (err) {
    console.warn('Failed to persist media for dual-window sync', err);
  }

  if (kind === 'image') {
    const dims = await loadImageDims(url);
    return {
      id,
      name: file.name,
      kind,
      url,
      mimeType: mime,
      duration: 5,
      width: dims.width,
      height: dims.height,
      fps: 30,
      nativeFps: 30,
      sizeBytes: file.size,
      createdAt: Date.now(),
    };
  }

  if (kind === 'audio') {
    const duration = await loadMediaDuration(url, 'audio');
    return {
      id,
      name: file.name,
      kind,
      url,
      mimeType: mime,
      duration,
      width: 0,
      height: 0,
      fps: 0,
      sizeBytes: file.size,
      createdAt: Date.now(),
    };
  }

  const meta = await loadVideoMeta(url);
  return {
    id,
    name: file.name,
    kind: 'video',
    url,
    mimeType: mime,
    duration: meta.duration,
    width: meta.width,
    height: meta.height,
    fps: meta.fps,
    nativeFps: meta.fps,
    sizeBytes: file.size,
    createdAt: Date.now(),
  };
}

function loadImageDims(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

function loadMediaDuration(url: string, kind: 'audio' | 'video'): Promise<number> {
  return new Promise((resolve) => {
    const el = document.createElement(kind);
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      resolve(Number.isFinite(el.duration) ? el.duration : 5);
      el.remove();
    };
    el.onerror = () => {
      resolve(5);
      el.remove();
    };
    el.src = url;
  });
}

function loadVideoMeta(url: string): Promise<{ duration: number; width: number; height: number; fps: number }> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      resolve({
        duration: Number.isFinite(video.duration) ? video.duration : 5,
        width: video.videoWidth || 1920,
        height: video.videoHeight || 1080,
        fps: 30,
      });
      video.remove();
    };
    video.onerror = () => {
      resolve({ duration: 5, width: 1920, height: 1080, fps: 30 });
      video.remove();
    };
    video.src = url;
  });
}

export function supportsWebCodecs(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';
}

export function supportsMediaRecorder(): boolean {
  return typeof MediaRecorder !== 'undefined';
}
